import { StorefrontApiError, storefront } from './storefront';

// Wire projection from PurchasePlanPort; quantities and totals are server-owned.
export interface PurchaseLine {
  item_id: string; title: string; qty: number; needed: string; unit: string;
  pack: string; remaining?: string | null; price_kopecks: number | null; image_url: string | null; brand: string | null;
  productIdentity: string; form: string; extra_unit: string | null;
  packOptions: Array<{ itemId: string; title: string; pack: string; price_kopecks: number | null; image_url: string | null; brand: string | null }>;
}
export interface PurchaseProjection {
  selection_id: string; plan_id: string; plan_version: number; cart_version: number;
  total_kopecks: number | null; total_complete: boolean;
  lines: PurchaseLine[];
  unresolved: Array<{ contributionId: string; itemId: string | null; title: string; reason: string; unit: string | null }>;
  exclusions: Array<{ productIdentity: string; form: string; scope: string }>;
  selections: Array<{ slotId: string; collectionId: string; revision: number; title: string; basePortions?: number }>;
  applied: boolean; history_lost: boolean; provenance_conflict: boolean;
  pending_guest_plan?: unknown; cycle_complete?: boolean;
}
export type PurchaseCommand = 'prepareSelection' | 'markAtHome' | 'unmarkAtHome' | 'selectPack' | 'resolveQuantity' | 'addSelection' | 'removeSelection' | 'replaceSelection' | 'applySelection' | 'confirmVerifiedPart';
export interface PurchaseRequest {
  selectionId: string; type: PurchaseCommand; payload: Record<string, unknown>;
  expectedPlanVersion: number; expectedCartVersion: number; operationId: string;
}
export interface PurchaseResult { status: string; projection: PurchaseProjection }
export function formatPurchaseMoney(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
    ? new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(value / 100)
    : 'Цена уточняется';
}
export function purchaseUnit(unit: string): string {
  return ({ g: 'г', ml: 'мл', pcs: 'шт.', pack: 'уп.' } as Record<string, string>)[unit] ?? unit;
}
function validProjection(value: unknown, scope: string): value is PurchaseProjection {
  if (!value || typeof value !== 'object') return false;
  const p = value as PurchaseProjection;
  return p.selection_id === scope && Number.isSafeInteger(p.plan_version) && p.plan_version >= 0
    && Number.isSafeInteger(p.cart_version) && p.cart_version >= 0
    && typeof p.plan_id === 'string' && Array.isArray(p.lines) && Array.isArray(p.unresolved)
    && Array.isArray(p.exclusions) && Array.isArray(p.selections) && typeof p.total_complete === 'boolean'
    && (p.total_kopecks === null || Number.isSafeInteger(p.total_kopecks) && p.total_kopecks >= 0);
}
export function createRecipePurchaseApi(opts: { base?: string; fetchImpl?: typeof fetch } = {}) {
  async function call(method: 'GET' | 'POST', scope: string, request?: PurchaseRequest): Promise<unknown> {
    const suffix = method === 'GET' ? `?${new URLSearchParams({ selectionId: scope })}` : '';
    const res = await (opts.fetchImpl ?? globalThis.fetch)(`${opts.base ?? storefront.base}/v1/recipe-purchase${suffix}`, {
      method, credentials: 'include', headers: { Accept: 'application/json', ...(request ? { 'Content-Type': 'application/json' } : {}) },
      ...(request ? { body: JSON.stringify(request) } : {}),
    });
    const payload: unknown = await res.json();
    if (!res.ok) throw new StorefrontApiError(`HTTP ${res.status}`, res.status, payload);
    return payload;
  }
  return {
    async read(scope: string): Promise<PurchaseProjection> {
      const r = await call('GET', scope) as { projection?: unknown };
      if (!validProjection(r?.projection, scope)) throw Error('invalid_purchase_projection');
      return r.projection;
    },
    async execute(request: PurchaseRequest): Promise<PurchaseResult> {
      const r = await call('POST', request.selectionId, request) as PurchaseResult;
      if (typeof r?.status !== 'string' || !validProjection(r.projection, request.selectionId)) throw Error('invalid_purchase_result');
      return r;
    },
  };
}
export const recipePurchaseApi = createRecipePurchaseApi();
